import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setAppLoading } from "actions/appStateActions";
import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { EMPTY_WALLET_ITEMS } from "actions/walletActions";
import IamApi from "api/IamApi";
import { RecentPaymentsContent } from "components/ezpay/secure/common/recent-payments-shared/RecentPaymentsContent";
import { reloadUpcomingPaymentWithState } from "components/ezpay/secure/common/recent-payments-shared/reloadUpcomingPaymentWithState";
import {
  BANK_ACCCOUNT_MODAL,
  COMMON_NAMESPACE,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  RECENT_PAYMENTS_NAMESPACE,
} from "constants/i18n-namespaces";
import { DONE, EMPTY, ERROR, IN_PROGRESS } from "constants/Status";
import { useCurrencyFormatter, usePaymentHistory } from "hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import routing from "routing";
import { getBiller } from "selectors";
import { getDeviceFingerprint, getIsMobile } from "selectors/appState";
import { getPayableSession } from "selectors/payableSession";
import { IPaymentRecord } from "types/Api/Payment/paymentRecord";
import { IContactInfoProperties } from "types/biller";
import {
  getDomain,
  mappingDonataionResponseToCharityState,
} from "utilities/helper";
import { PaymentContent } from "../recent-payments-shared/PaymentContent";
export const MAX_RECENT_PAYMENTS = 3;

export const PayableRecentPayments = () => {
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] =
    useState<boolean>(false);
  const deviceFingerprint = useSelector(getDeviceFingerprint);
  const payableSession = useSelector(getPayableSession);
  const [cancelState, setCancelState] = useState(EMPTY);
  const [cancelErrorMessage, setCancelErrorMessage] = useState<string>();

  const [loadingStatus, setLoadingStatus] = useState(false);

  const { t } = useTranslation([
    RECENT_PAYMENTS_NAMESPACE,
    PAYMENT_METHOD_MODAL_NAMESPACE,
    BANK_ACCCOUNT_MODAL,
  ]);
  const isMobile = useSelector(getIsMobile);

  const { loadUpcomingPayment, getUpcomingPayment, getRecentPayments } =
    usePaymentHistory(true);

  const dispatch = useDispatch();

  const biller = useSelector(getBiller);
  const navigate = useNavigate();
  const location = useLocation();
  const currencyFormatter = useCurrencyFormatter();

  // Check if navigated from item-voided-archived page
  const isFromVoidedArchived = location.state?.from === "item-voided-archived";
  const upcomingPayment = getUpcomingPayment();
  const recentPayments = getRecentPayments();

  const contactUs = biller?.properties?.find((element) => {
    return element.name === "InstanceConfiguration";
  })?.properties as IContactInfoProperties;

  const cancelPaymentIntent = async (
    paymentId: string,
    paymentIntentId: string,
  ) => {
    dispatch(setAppLoading(true));
    try {
      setCancelState(IN_PROGRESS);
      await IamApi.ezpay.cancelPayableUpcomingPayment(
        paymentId,
        paymentIntentId,
        biller.clientId,
        payableSession.instanceInfo.realm,
      );
      setCancelState(DONE);
    } catch (error: any) {
      setCancelState(ERROR);
      setCancelErrorMessage(error.message);
    }
    dispatch(setAppLoading(false));
  };

  const reloadUpcomingPayment = () =>
    reloadUpcomingPaymentWithState({
      clientId: biller.clientId,
      realm: payableSession.instanceInfo.realm,
      loadUpcomingPayment,
      setLoadingStatus,
      setConfirmCancelModalOpen,
      setCancelState,
      setCancelErrorMessage,
    });

  const createPaymentIntentContent = (
    payment: IPaymentRecord,
    isExpanded: boolean,
  ) => {
    const charities = mappingDonataionResponseToCharityState(
      payment.donations,
      payableSession?.charities ?? [],
    );
    const selectedCharities = charities?.filter((char) => char.selected);

    return (
      <PaymentContent
        payment={payment}
        upcomingPayment={upcomingPayment}
        isMobile={isMobile}
        selectedCharities={selectedCharities}
        isUpcomingCancellable={!!upcomingPayment?.isCancellable}
        contactUs={contactUs}
        currencyFormatter={currencyFormatter}
        onCancelClick={() => setConfirmCancelModalOpen(true)}
        isExpanded={isExpanded}
      />
    );
  };

  const handleBackButtonClick = async () => {
    try {
      if (isFromVoidedArchived) {
        navigate(routing.itemVoidedArchived.index());
        return;
      }

      if (!payableSession.paymentIntentId) {
        dispatch(setAppLoading(true));
        dispatch({ type: ACTION.ADD_CHARITIES, payload: [] });

        payableSession.payable &&
          (await IamApi.ezpay.postPayableInitiateIntent(
            payableSession.payable.krn,
            deviceFingerprint,
            navigator.userAgent,
          ));

        const domain = getDomain();
        const newPayableSession =
          await IamApi.ezpay.getPayableFromSession(domain);

        dispatch(setPayableSession(newPayableSession));
        dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
        dispatch({ type: EMPTY_WALLET_ITEMS });
      }

      navigate(routing.payableDashboard.index());
    } catch (error) {
      console.error("Error on back button click ", error);
    } finally {
      dispatch(setAppLoading(false));
    }
  };

  return (
    <div className="payable-payment-receipt-frame" style={{ gap: 0 }}>
      <div
        className="common-header payment-receipt-frame-header"
        style={{ width: "100%" }}
      >
        <div className="back-to-payment-dashboard">
          <button
            className="ez-pay-button-link"
            onClick={handleBackButtonClick}
            data-cy="back-to-payments"
          >
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            <span>
              {" "}
              {isFromVoidedArchived
                ? t("back", { ns: COMMON_NAMESPACE })
                : t("back-to-payment-dashboard")}
            </span>
          </button>
        </div>
      </div>

      <RecentPaymentsContent
        loadingStatus={loadingStatus}
        upcomingPayment={upcomingPayment}
        recentPayments={recentPayments}
        createPaymentIntentContent={createPaymentIntentContent}
        biller={biller}
        confirmCancelModalOpen={confirmCancelModalOpen}
        isMobile={isMobile}
        cancelState={cancelState}
        cancelErrorMessage={cancelErrorMessage}
        onCancelModalClose={() => setConfirmCancelModalOpen(false)}
        onConfirmCancel={() =>
          cancelPaymentIntent(
            upcomingPayment.id,
            upcomingPayment.paymentIntentId,
          )
        }
        onReload={reloadUpcomingPayment}
      />
    </div>
  );
};

export default PayableRecentPayments;
